import { useAccount } from "@/hooks/useAccount";
import { fmtCurrency, fmtPct, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Landmark, ShieldCheck, CalendarClock, Activity } from "lucide-react";

export default function Funds() {
  const { data, isLoading } = useAccount();
  if (isLoading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 rounded-2xl" /></div>;

  const funds = data?.funds || [];

  return (
    <div className="space-y-8" data-testid="funds-page">
      <div className="fade-up">
        <p className="overline mb-1">Portfolio</p>
        <h1 className="font-serif text-4xl font-bold text-slate-900">Funds</h1>
        <p className="text-slate-500 mt-1">The funds and strategies your capital is allocated to.</p>
      </div>

      {funds.length === 0 ? (
        <Card className="p-12 text-center bg-white border-slate-200 text-slate-500">No funds allocated yet.</Card>
      ) : (
        <div className="space-y-6">
          {funds.map((f, i) => {
            const gain = f.current_value - f.principal;
            const pct = f.principal ? (gain / f.principal) * 100 : 0;
            return (
              <Card key={f.id || i} data-testid={`fund-${i}`} className="overflow-hidden bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] fade-up">
                <div className="p-6 bg-[#15803d] text-white flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
                      <Landmark className="w-6 h-6" strokeWidth={1.75} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/60">{f.code}</p>
                      <h3 className="font-serif text-2xl font-bold">{f.name}</h3>
                    </div>
                  </div>
                  <Badge className="bg-white/20 text-white border-0">{f.status}</Badge>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-6">{f.strategy}</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                      <p className="overline mb-1">Principal Invested</p>
                      <p className="text-xl font-bold tabular-nums text-slate-900">{fmtCurrency(f.principal)}</p>
                    </div>
                    <div>
                      <p className="overline mb-1">Current Value</p>
                      <p className="text-xl font-bold tabular-nums text-slate-900">{fmtCurrency(f.current_value)}</p>
                    </div>
                    <div>
                      <p className="overline mb-1">Return on Principal</p>
                      <p className={`text-xl font-bold tabular-nums ${gain >= 0 ? "text-[#059669]" : "text-[#dc2626]"}`}>
                        {fmtCurrency(gain)} <span className="text-sm">({fmtPct(pct)})</span>
                      </p>
                    </div>
                    <div>
                      <p className="overline mb-1">Since</p>
                      <p className="text-xl font-bold tabular-nums text-slate-900">{fmtDate(f.inception_date)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-slate-100 text-sm text-slate-500">
                    <span className="flex items-center gap-1.5"><Activity className="w-4 h-4" strokeWidth={1.75} /> Risk: {f.risk}</span>
                    <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" strokeWidth={1.75} /> Managed &amp; hedged</span>
                    <span className="flex items-center gap-1.5"><CalendarClock className="w-4 h-4" strokeWidth={1.75} /> Daily NAV updates</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
